import React, { useMemo, useState } from 'react'
import { Collapse, Tooltip } from '@mui/material'
import styled, { css } from 'styled-components'
import Select from 'components/atoms/Select'
import { isNilOrEmpty, isNotNilOrEmpty } from 'utils/ramda'
import { useSelector } from 'react-redux'
import { getFileTypes } from 'ducks/files/selectors'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { selectBpUnits } from 'ducks/units/selectors'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import { Description, ApartmentOutlined, Event } from '@mui/icons-material'
import EnhancedFileTypeSelector from 'features/bpDetails/Components/files/EnhancedFileTypeSelector'
import Input from 'components/atoms/Input'
import AddUsersToFile from 'features/bpDetails/Components/files/AddUsersToFile'
import AttachTagsToUploadingFile from 'features/bpDetails/Components/files/AttachTagsToUploadingFiles'

const defaultValues = {
  fileCategory: '',
  fileType: '',
  units: [],
  expirationDate: '',
  userIds: [],
  tagIds: []
}

const informationExchangeCategoryName = 'FRS Information Exchange'

const BulkEditForm = ({ open, onChange, isUnit, isPrivate, isAI }) => {
  const [values, setValues] = useState(defaultValues)
  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const bpUnits = useSelector(selectBpUnits)
  const bp = useSelector(getSelectedBp)

  const isValid = useMemo(() => {
    // Check if ANY field has a value, not just specific combinations
    return Object.values(values).some(value =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    )
  }, [values])

  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const handleCategoryChange = (_, value) => {
    setValues(prev => ({
      ...prev,
      fileType: ''
    }))
    setTimeout(() => {
      setValues(prev => ({
        ...prev,
        fileCategory: value
      }))
    }, 0)
  }

  const handleTypeChange = (_, value) => {
    setValues(prev => ({
      ...prev,
      fileType: value
    }))
  }

  const categoriesOptions = fileCategories
    .filter(cat => cat.name !== informationExchangeCategoryName)
    .map(type => ({
      label: type.name,
      value: type.id
    }))


  const unitsOptions = bpUnits
    // .filter(unit => !isResidentialUnit(unit.buildingType))
    .map(type => ({
      label: type.name,
      value: type.id
    }))

  const handleSaveChanges = () => {
    onChange(values)
  }

  const handleTypeAndCategoryChange = (category, type) => {
    category !== values.fileCategory && handleCategoryChange('', category)
    type !== values.fileType && handleTypeChange('', type)
  }

  return (
    <Collapse in={open}>
      <BulkEditWrapper>
        <BulkEditHeader>
          <BulkEditTitle>Bulk Edit Selected Files</BulkEditTitle>
          <BulkEditDescription>
            Apply the same properties to all selected files
          </BulkEditDescription>
        </BulkEditHeader>

        <BulkEditContent>
          <BulkEditRow isUnit={isUnit} isAI={isAI}>
            {!isAI && (
              <BulkEditField className="file-type">
                <BulkEditLabel>
                  <Description style={{ fontSize: 14, marginRight: 4 }} />
                  File Type
                </BulkEditLabel>
                <EnhancedFileTypeSelector
                  value={values.fileType}
                  onSelect={handleTypeAndCategoryChange}
                  placeholder="Search and select file type..."
                />
              </BulkEditField>
            )}

            {!isUnit && (
              <BulkEditField>
                <BulkEditLabel>
                  <ApartmentOutlined style={{ fontSize: 14, marginRight: 4 }} />
                  Units
                </BulkEditLabel>
                <Select
                  noMargin
                  multiple
                  onChange={handleValueChange}
                  value={values.units}
                  selectAll
                  name='units'
                  disabled={isNilOrEmpty(unitsOptions)}
                  options={unitsOptions}
                  placeholder="Select units..."
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) return "Select units..."

                    const selectedLabels = unitsOptions
                      .filter(opt => selected.includes(opt.value))
                      .map(opt => opt.label)

                    if (selectedLabels.length <= 3) {
                      return selectedLabels.join(", ")
                    }

                    return `${selectedLabels.slice(0, 3).join(", ")} +${selectedLabels.length - 3} more`
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300, // scroll la dropdown
                      },
                    },
                  }}
                />
              </ BulkEditField >

            )}

            {!isPrivate && (
              <BulkEditField>
                <BulkEditLabel>
                  <Event style={{ fontSize: 14, marginRight: 4 }} />
                  Expiry Date
                </BulkEditLabel>
                <Input
                  nomarginbottom
                  value={values.expirationDate}
                  name='expirationDate'
                  type='date'
                  onChange={handleValueChange}
                />
              </BulkEditField>
            )}

            {!isUnit && (
              <>
                <BulkEditField>
                  <BulkEditLabel>Share</BulkEditLabel>
                  <AddUsersToFile
                    isUpload
                    value={values.userIds}
                    savedCount={values.userIds?.length || 0}
                    onSave={value => {
                      handleValueChange('userIds', value)
                    }}
                  />
                </BulkEditField>

                <BulkEditField>
                  <BulkEditLabel>Tags</BulkEditLabel>
                  <AttachTagsToUploadingFile
                    savedCount={values.tagIds?.length || 0}
                    onSave={value => {
                      handleValueChange('tagIds', value)
                    }}
                  />
                </BulkEditField>
              </>
            )}

            <BulkEditField>
              <BulkEditLabel>Apply Changes</BulkEditLabel>
              <ApplyButton
                onClick={isValid ? handleSaveChanges : () => {}}
                disabled={!isValid}
              >
                <CheckOutlinedIcon />
                Apply to Selected
              </ApplyButton>
            </BulkEditField>
          </BulkEditRow>
        </BulkEditContent>
      </BulkEditWrapper>
    </Collapse>
  )
}

export default BulkEditForm

const BulkEditWrapper = styled.div`
  background: ${({ theme }) => theme.colors.lighterGrey || '#f8f9fa'};
  border: 2px solid ${({ theme }) => theme.colors.secondary || '#33becc'};
  border-radius: 8px;
  margin: 0 0 16px 0;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 100;
`

const BulkEditHeader = styled.div`
  background: ${({ theme }) => theme.colors.secondary || '#33becc'};
  padding: 14px 24px;
  color: white;
`

const BulkEditTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
  color: white;
`

const BulkEditDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
`

const BulkEditContent = styled.div`
  padding: 20px 24px;
`

const BulkEditRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 100px 100px 140px;
  gap: 20px;
  align-items: end;

  ${({ isUnit }) =>
    isUnit &&
    css`
      grid-template-columns: 2fr 1fr 140px;
    `}

  ${({ isAI, isUnit }) =>
    isAI &&
    !isUnit &&
    css`
      grid-template-columns: 1fr 1fr 100px 100px 140px;
    `}

  ${({ isAI, isUnit }) =>
    isAI &&
    isUnit &&
    css`
      grid-template-columns: 1fr 140px;
    `}

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
`

const BulkEditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.file-type {
    min-width: 300px;
  }
`

const BulkEditLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  height: 16px;

  svg {
    margin-right: 4px;
  }
`

const ApplyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 10px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 36px;
  min-width: 160px;
  width: 100%;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.secondaryDark || theme.colors.secondary};
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.grey[500]};
    color: ${({ theme }) => theme.colors.grey[700]};
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 16px;
  }
`
